/* eslint-disable jsx-a11y/mouse-events-have-key-events */
/* eslint-disable jsx-a11y/interactive-supports-focus */
/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import { omit } from 'lodash';
import { classnames as cx } from '@ba-ui-toolkit/utils';
import SVGIcon from '@ba-ui-toolkit/svgicon';
import ToolTip from '@ba-ui-toolkit/tooltip';
import close from '@ba-ui-toolkit/ba-graphics/dist/icons-js/close_16';
import { Flyout } from '@ba-ui-toolkit/flyout';
import Toolbar from '@ba-ui-toolkit/toolbar';
import { DragSource, DropTarget } from 'react-dnd';
import ItemTypes from './ItemTypes';
import TabLabel from './TabLabel';

import './Tabs.scss';

const tabSource = {
	beginDrag(props) {
		props.isDraggingCallback(true);

		return {
			id: props.panelId,
			index: props.index,
		};
	},
};

const tabTarget = {
	hover(props, monitor, component) {
		const dragIndex = monitor.getItem().index;
		const hoverIndex = props.index;

		// Don't replace items with themselves
		if (dragIndex === hoverIndex) {
			return;
		}

		// Determine rectangle on screen
		const hoverBoundingRect = findDOMNode(component).getBoundingClientRect();

		if (props.vertical) {
			const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

			// Determine mouse position
			const clientOffset = monitor.getClientOffset();

			// Get pixels to the top
			const hoverClientY = clientOffset.y - hoverBoundingRect.top;

			// Only perform the move when the mouse has crossed half of the items height
			// When dragging downwards, only move when the cursor is below 50%
			// When dragging upwards, only move when the cursor is above 50%

			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return;
			}

			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return;
			}
		} else {
			// Get horizontal middle
			const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;

			// Determine mouse position
			const clientOffset = monitor.getClientOffset();

			if (clientOffset === null) {
				return;
			}

			// Get pixels to the left
			const hoverClientX = clientOffset.x - hoverBoundingRect.left;

			// Only perform the move when the mouse has crossed half of the items width
			// When dragging downwards, only move when the cursor is below 50%
			// When dragging upwards, only move when the cursor is above 50%

			// Dragging left
			if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) {
				component.handler.component.setState({ dragDivider: 'left' });

				return;
			}

			// Dragging right
			if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) {
				component.handler.component.setState({ dragDivider: 'right' });

				return;
			}
		}

		// Time to actually perform the action
		props.moveTab(dragIndex, hoverIndex, false, props.keyValue);

		// Note: we're mutating the monitor item here!
		// Generally it's better to avoid mutations,
		// but it's good here for the sake of performance
		// to avoid expensive index searches.
		monitor.getItem().index = hoverIndex;
	},

	drop(props, monitor) {
		props.isDraggingCallback(false);
		// perform moveTab but inform that the tab has been dropped
		const dragIndex = monitor.getItem().index;
		const hoverIndex = props.index;
		props.moveTab(dragIndex, hoverIndex, true, props.keyValue);
	},
};

@DropTarget(ItemTypes.TAB, tabTarget, (connect, monitor) => ({
	connectDropTarget: connect.dropTarget(),
	isOver: monitor.isOver(),
}))
@DragSource(ItemTypes.TAB, tabSource, (connect, monitor) => ({
	connectDragSource: connect.dragSource(),
	isDragging: monitor.isDragging(),
}))
export class TabItemDnD extends Component {
	static propTypes = {
		connectDragSource: PropTypes.func.isRequired,
		connectDropTarget: PropTypes.func.isRequired,
	};
	render() {
		const { connectDragSource, connectDropTarget, ...other } = this.props;

		return connectDragSource(
			connectDropTarget(
				<div className="ba-common-tabList__itemDraggable">
					{' '}
					<TabItem {...other} />{' '}
				</div>
			)
		);
	}
}

export default class TabItem extends Component {
	static propTypes = {
		className: PropTypes.string,
		panelId: PropTypes.string.isRequired,
		setSelectedNode: PropTypes.func.isRequired,
		setFocusNode: PropTypes.func.isRequired,
		onClick: PropTypes.func.isRequired,
		isDragging: PropTypes.bool,
		isOver: PropTypes.bool,
		index: PropTypes.number.isRequired,
		tooltipOrient: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),

		selected: PropTypes.bool,
		deleteable: PropTypes.bool,
		editable: PropTypes.bool,
		disabled: PropTypes.bool,
		vertical: PropTypes.bool,
		isIconBeforeLabel: PropTypes.bool,
		iconPosition: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),

		label: PropTypes.string,
		subTitle: PropTypes.string,
		title: PropTypes.string,
		keyValue: PropTypes.string,
		icon: PropTypes.string,
		backgroundColor: PropTypes.string,
		hoverColor: PropTypes.string,
		deleteBackgroundColor: PropTypes.string,
		deleteIconColor: PropTypes.string,
		textColor: PropTypes.string,
		linePositionClass: PropTypes.string,
		lineColor: PropTypes.string,
		iconColor: PropTypes.string,
		textDir: PropTypes.oneOf(['', 'ltr', 'rtl', 'auto']),

		onEnter: PropTypes.func,
		onLeave: PropTypes.func,
		onEditLabel: PropTypes.func,
		deleteHandler: PropTypes.func,
		toolbar: PropTypes.array,
		incrementTab: PropTypes.func,
		onDragStart: PropTypes.func,
		onDeletePrompt: PropTypes.func,
		disableDrag: PropTypes.func,
		enableDrag: PropTypes.func,
		assignTabLabelRef: PropTypes.func,
		onLabelBlur: PropTypes.func,

		tabButton: PropTypes.bool,
	};

	static defaultProps = {
		isIconBeforeLabel: false,
		editable: false,
		tooltipOrient: 'bottom',
		iconPosition: 'right',
	};

	constructor(props) {
		super(props);
		this.state = {
			label: this.props.label,
			flyoutOpen: false,
			editActive: false,
			themeHover: false,
		};
	}

	onKeyPress = event => {
		if (event.which === 13 /* Enter */) {
			event.preventDefault();
			event.target.blur();
		}
	};

	onKeyDown = e => {
		const { deleteHandler, onDeletePrompt } = this.props;

		switch (e.keyCode) {
			// enter
			case 13:
				e.preventDefault();
				e.stopPropagation();
				this.tabLabel && this.tabLabel.focusOnLabel();
				if (this.props.toolbar) {
					this.onDoubleClickHandler();
				}
				break;
			// delete
			case 46:
				e.preventDefault();
				e.stopPropagation();
				if (this.props.deleteable) {
					if (onDeletePrompt) {
						this.deleteCallback();
					} else {
						deleteHandler && deleteHandler();
					}
				}
				break;
			// ctrl/cmd + ->
			case 39:
				if (e.ctrlKey || e.metaKey) {
					this.handleTabIncrement(e, 1);
				}
				this.closeToolbar();
				break;
			// ctrl/cmd + <-
			case 37:
				if (e.ctrlKey || e.metaKey) {
					this.handleTabIncrement(e, -1);
				}
				this.closeToolbar();
				break;

			default:
		}
	};

	onDoubleClickHandler = () => {
		this.setState(state => ({ flyoutOpen: !state.flyoutOpen }));
	};

	/**
	 * Moves a tab via the keyboard.  Finds the tab index and calls the incrementTab callback.
	 *
	 * @param e {event} - keyboard event
	 * @param direction {number} - direction to move the tab, 1 for right and -1 for left.
	 */
	handleTabIncrement(e, direction) {
		const { incrementTab } = this.props;
		e.preventDefault();
		e.stopPropagation();
		if (incrementTab) {
			const idx = e.target.getAttribute('data-index');
			incrementTab(parseInt(idx, 10), direction, this.props.keyValue);
		}
	}

	closeToolbar = () => {
		this.setState({ flyoutOpen: false });
	};

	changeHandler(event, id) {
		const newText = event.target.textContent || '\u00A0'; // If newText isEmpty, newText defaults to a blank space to ensure label prop from Tabs parent component
		this.props.onEditLabel(id, newText);
		event.target.scrollLeft = 0;
	}

	assignSvgIconColor(node, color) {
		if (node && color) {
			node.style.color = color;
		}
	}

	focusHandler = value => {
		this.setState({ editActive: value });
	};

	deleteCallback() {
		return this.props.onDeletePrompt(this.props.deleteHandler);
	}
	themeHoverOn = () => {
		this.setState({ themeHover: true });
	};
	themeHoverOff = () => {
		this.setState({ themeHover: false });
	};

	render() {
		const {
			selected,
			label,
			textDir,
			subTitle,
			editable,
			setSelectedNode,
			setFocusNode,
			panelId,
			onEnter,
			onLeave,
			index,
			title,
			deleteHandler,
			onDeletePrompt,
			disabled,
			isDragging,
			isOver,
			icon,
			isIconBeforeLabel,
			iconPosition,
			iconColor,
			disableDrag,
			enableDrag,
			tabButton,
			vertical,
			tooltipOrient,
			...others
		} = this.props;
		let onClick;

		if (this.props.disabled === true) {
			onClick = e => {
				e.stopPropagation();
			};
		} else {
			onClick = this.props.onClick;
		}
		const className = cx(
			'ba-common-tabList__item',
			{
				selected,
				label: !!label,
				disabled,
				editable,
				dragging: isDragging,
				dragDivider: isOver && this.state.dragDivider,
				tabButton,
			},
			isOver && this.state.dragDivider ? `dragDivider-${this.state.dragDivider}` : undefined
		);

		let lineStyle;

		if (selected) {
			switch (this.props.linePositionClass) {
				case 'line-position-top':
					lineStyle = `0px 3px 0 ${this.props.lineColor} inset`;
					break;
				case 'line-position-left':
					lineStyle = `3px 0px 0 ${this.props.lineColor} inset`;
					break;
				case 'line-position-right':
					lineStyle = `-3px 0px 0 ${this.props.lineColor} inset`;
					break;
				case 'line-position-bottom':
					lineStyle = `0px -3px 0 ${this.props.lineColor} inset`;
					break;
				default:
					break;
			}
		}

		const themeBackgroundColor =
			this.state.themeHover && this.props.hoverColor
				? this.props.hoverColor
				: this.props.backgroundColor;
		const style = {
			...(this.props.backgroundColor || this.props.hoverColor
				? { backgroundColor: themeBackgroundColor }
				: {}),
			color: this.props.textColor,
			...(this.props.lineColor ? { boxShadow: lineStyle } : {}),
		};
		const deleteStyles = {
			...(this.props.deleteBackgroundColor
				? { backgroundColor: this.props.deleteBackgroundColor }
				: {}),
		};

		const cProps = {
			...omit(
				others,
				'lineColor',
				'backgroundColor',
				'hoverColor',
				'assignTabLabelRef',
				'id',
				'keyValue',
				'linePositionClass',
				'moveTab',
				'onEditLabel',
				'onLabelBlur',
				'pinnable',
				'resizable',
				'value',
				'incrementTab',
				'isDraggingCallback',
				'deleteable',
				'keepTabContent'
			),
			onFocus: e => {
				setFocusNode(e.currentTarget);
			},
			onBlur: e => {
				setFocusNode(null);
				onLeave(e.currentTarget);
			},
			onKeyUp: e => onLeave(e.currentTarget),
			onKeyDown: this.onKeyDown,
			onMouseEnter: e => onEnter(e.currentTarget),
			tabIndex: selected ? 0 : -1,
			'aria-selected': selected,
			'aria-controls': panelId,
			ref: tab => {
				this.tabEl = tab;
				if (selected) {
					setSelectedNode(tab);
				}
				this.props.assignTabLabelRef(tab);
			},
			className,
			onClick,
		};

		const flyout = this.state.flyoutOpen && this.props.toolbar && this.props.selected && (
			<Flyout
				className="tab-item-flyout-container"
				placement="bottom"
				padding="default"
				hideArrow
				domNodeToAttachTo={document.body}
				onClose={this.closeToolbar}
			>
				<Toolbar tabIndex={0}>
					{this.props.toolbar.map((element, i) => (
						<SVGIcon
							iconId={element.icon.id}
							size="small"
							key={i}
							className="ba-common-toolbar__item"
							onClick={() => {
								element.handler();
								this.setState(state => ({ flyoutOpen: !state.flyoutOpen }));
							}}
							focusable
							tabIndex={0}
						/>
					))}
				</Toolbar>
			</Flyout>
		);

		let iconPositionClass = '';
		if (isIconBeforeLabel || iconPosition === 'left') {
			iconPositionClass = 'is-left-positioned-icon';
		} else if (iconPosition === 'top') {
			iconPositionClass = 'is-top-positioned-icon';
		} else if (iconPosition === 'bottom') {
			iconPositionClass = 'is-bottom-positioned-icon';
		}
		const isOnlyIconClass = !label ? ' is-only-icon-tab' : '';
		const labelIconSize = label ? 'small' : 'normal';
		const iconClassName = this.props.className ? this.props.className : '';

		const labelIcon = icon ? (
			<SVGIcon
				className={`ba-common-tabList__labelIcon ${iconClassName} ${isOnlyIconClass} ${iconPositionClass}`}
				ref={node => {
					this.assignSvgIconColor(findDOMNode(node), iconColor);
				}}
				iconId={icon}
				size={labelIconSize}
			/>
		) : (
			''
		);

		const dismissButton = !this.state.editActive && (
			<SVGIcon
				iconId={close.id}
				size="small"
				ref={node => {
					this.assignSvgIconColor(findDOMNode(node), this.props.deleteIconColor);
				}}
				onClick={e => {
					e.stopPropagation();
					if (onDeletePrompt) {
						this.deleteCallback();
					} else {
						deleteHandler();
					}
				}}
			/>
		);

		return (
			<ToolTip
				title={title || this.state.label}
				delay
				orient={tooltipOrient}
				display={vertical ? 'block' : 'inline-block'}
				className="flyout-wrapper ba-common-tabList__tooltip"
				onDoubleClick={this.onDoubleClickHandler}
				disabled={isDragging || this.state.flyoutOpen}
				onKeyDown={this.onKeyDown}
			>
				<div
					data-tid={`tabItem${index}`}
					{...cProps}
					onMouseOver={this.themeHoverOn}
					onMouseLeave={this.themeHoverOff}
					style={style}
					role="tab"
					data-index={index}
				>
					{isOver}
					<div className="ba-common-tabList__label__container">
						<div
							className={`ba-common-tabList__label${isOnlyIconClass}`}
							id={`label-${index}`}
							onKeyPress={this.onKeyPress}
							onClick={this.onLabelClick}
						>
							{label ? (
								<TabLabel
									className={`ba-common-tabList__labelText ${iconPositionClass}`}
									contentEditable={!disabled && selected && editable !== false}
									changeHandler={event => {
										this.changeHandler(event, this.props.keyValue);
									}}
									focusHandler={this.focusHandler}
									ref={input => {
										this.tabLabel = input;
									}}
									label={label}
									textDir={textDir}
									tabButton={tabButton}
									disableDrag={disableDrag}
									enableDrag={enableDrag}
									onLabelBlur={() => {
										this.props.onLabelBlur();
									}}
									hideDismiss={this.hideDismiss}
								/>
							) : (
								''
							)}
							{labelIcon}
						</div>
						{dismissButton && !disabled ? (
							<span
								className="ba-common-tabList__itemIcon close"
								style={deleteStyles}
							>
								{dismissButton}
							</span>
						) : null}
						<div className="subTitle">{subTitle}</div>
					</div>
				</div>
				{flyout}
			</ToolTip>
		);
	}
}
